/**
 * ProductionPipelineBar — the garment home's glanceable "where are your pieces"
 * bar: one proportional segment per production stage (Cutting → Sewing →
 * Finishing → Packing → Ready to ship), each labelled with its piece count, plus
 * a legend. Fed by `orderBook.overview.pipeline` (factory-wide WIP rollup).
 *
 * More glanceable than any number — it's the factory floor in one bar — and it
 * carries multiple figures, which is why the garment home leads with it instead
 * of a trend chart.
 *
 * @see prototype/garment/home-simple.html — the pipeline bar baseline
 */
import type { orderBook as contract } from "@flint/api-contract";

type Pipeline = contract.OverviewOutput["pipeline"];

const STAGES: { key: keyof Omit<Pipeline, "total">; label: string; bar: string; dot: string }[] = [
  { key: "cutting", label: "Cutting", bar: "bg-slate-500", dot: "bg-slate-500" },
  { key: "sewing", label: "Sewing", bar: "bg-sky-700", dot: "bg-sky-700" },
  { key: "finishing", label: "Finishing", bar: "bg-sky-600", dot: "bg-sky-600" },
  { key: "packing", label: "Packing", bar: "bg-sky-400", dot: "bg-sky-400" },
  { key: "readyToShip", label: "Ready to ship", bar: "bg-emerald-600", dot: "bg-emerald-600" },
];

const intIN = (n: number) => n.toLocaleString("en-IN");

export function ProductionPipelineBar({ pipeline }: { pipeline: Pipeline }) {
  const active = STAGES.filter((s) => pipeline[s.key] > 0);

  return (
    <div className="mt-5">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-semibold text-ink-2">Where your pieces are</span>
        <span className="text-xs text-muted">
          Cutting → Sewing → Finishing → Packing → Ready to ship
        </span>
      </div>

      {pipeline.total === 0 ? (
        <p className="text-sm text-muted">No pieces on the floor right now.</p>
      ) : (
        <>
          <div
            className="flex h-11 gap-0.5 overflow-hidden rounded-lg"
            role="img"
            aria-label={active
              .map((s) => `${intIN(pipeline[s.key])} ${s.label.toLowerCase()}`)
              .join(", ")}
          >
            {active.map((s) => (
              <div
                key={s.key}
                className={`grid place-items-center text-sm font-bold text-white ${s.bar}`}
                style={{ flexGrow: pipeline[s.key], flexBasis: 0, minWidth: 0 }}
              >
                <span className="tabular-nums">{intIN(pipeline[s.key])}</span>
              </div>
            ))}
          </div>

          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5">
            {STAGES.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted">
                <i className={`h-2 w-2 shrink-0 rounded-sm ${s.dot}`} />
                {s.label} <b className="font-semibold text-ink-2">{intIN(pipeline[s.key])}</b>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
